import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useQuery, useMutation } from 'react-query';
import { useHistory, useLocation } from 'react-router';
import { Backdrop } from '@omilia/modal';

// @ts-ignore
import ErrorHandler from 'store/utils/ErrorHandler';
// @ts-ignore
import Notify from 'store/utils/Notify';
// @ts-ignore
import { getProjectBranch, getProject } from 'store/services/project/selectors';
// @ts-ignore
import messages from 'constants/messages';
import { PullRequestResponse } from 'hooks/useVersionControl/types';
import {
  compareParentRequest,
  getParentConflictsRequest,
  updateFromParentRequest,
  mergeRequest,
} from './api';
import { CompareParent, Props, Conflicts, MergeRequest } from './types';
import { refreshNLUState } from '../../utils';
import CheckMergeBranchLoadingModal from './modals/CheckMergeBranchLoadingModal';
import FailureModal from './modals/FailureModal';
import UpdateRequiredModal from './modals/UpdateRequiredModal';
import UpdateFromParentSuccessModal from './modals/UpdateFromParentSuccessModal';
import UpdateBranchLoadingModal from './modals/UpdateBranchLoadingModal';
import MergeRequestBranchModal from './modals/MergeRequestBranchModal';
import ResolveConfilctsModal from './modals/ResolveConflictsModal';
import MergeBranchLoadingModal from './modals/MergeBranchLoadingModal';

const MergeBranchModal = ({
  onCancel,
  projectBranch,
  project,
  refreshNLUState,
}: Props) => {
  const history = useHistory();
  const { pathname } = useLocation();

  const { account, slug } = project;
  const branchName = projectBranch?.name ?? '';

  const [conflictsIdentified, setConflictsIdentified] = useState(false);
  const {
    data: compareParentData,
    error: compareParentError,
    isFetching: isFetchingCompareParent,
  } = useQuery<CompareParent, 'compareParent'>(
    'compareParent',
    compareParentRequest,
    {
      retry: false,
      refetchOnWindowFocus: false,
      cacheTime: undefined,
    },
  );

  const handleMergeRequestSuccess = ({ parent: { slug } }: MergeRequest) => {
    history.push(`/${account}/${slug}/`);

    Notify.success({
      title: messages.VERSION_CONTROL.MERGE_MODAL.MERGE_REQUEST_SUCCESS,
    });
    onCancel();
  };

  const [
    createMergeRequest,
    { status: mergeRequestStatus, error: mergeRequestError },
  ] = useMutation(mergeRequest, {
    onSuccess: handleMergeRequestSuccess,
  });

  const [
    updateFromParent,
    { status: updateFromParentStatus, error: updateFromParentError },
  ] = useMutation<{ data: PullRequestResponse }>(updateFromParentRequest, {
    onSuccess: ({ data }) => {
      refreshNLUState(data);
    },
  });

  const handleGetParentConflictsSuccess = (data: Conflicts) => {
    const { conflicts } = data;

    if (conflicts?.length === 0) {
      updateFromParent();
    } else {
      setConflictsIdentified(true);
    }
  };

  const [
    getParentConflicts,
    { status: getParentConflictsStatus, error: getParentConflictsError },
  ] = useMutation(getParentConflictsRequest, {
    onSuccess: handleGetParentConflictsSuccess,
  });

  const hasError = compareParentError || mergeRequestError;
  const isMergeRequestLoading = mergeRequestStatus === 'loading';
  const isGetParentConflictsLoading = getParentConflictsStatus === 'loading';
  const isUpdateFromParentLoading = updateFromParentStatus === 'loading';
  const updateFromParentSucceed = updateFromParentStatus === 'success';

  const hasParentCommitsAhead =
    Number(compareParentData?.parent_commits_ahead) > 0;

  const error = ErrorHandler.getDetailError(
    mergeRequestError ||
      getParentConflictsError ||
      updateFromParentError ||
      compareParentError,
  );

  const renderModals = () => {
    if (isFetchingCompareParent) {
      return <CheckMergeBranchLoadingModal branchName={branchName ?? ''} />;
    }

    if (isMergeRequestLoading) {
      return <MergeBranchLoadingModal branchName={branchName ?? ''} />;
    }

    if (isGetParentConflictsLoading || isUpdateFromParentLoading) {
      return <UpdateBranchLoadingModal />;
    }

    if (updateFromParentSucceed) {
      return <UpdateFromParentSuccessModal onCancel={onCancel} />;
    }

    if (conflictsIdentified) {
      return (
        <ResolveConfilctsModal
          onCancel={onCancel}
          onResolveConflicts={() => {
            history.push(
              `/${account}/${slug}/resolve-conflicts?from=${encodeURIComponent(
                pathname,
              )}&parent=true`,
            );
          }}
        />
      );
    }

    if (hasError) {
      return (
        <FailureModal
          error={
            error ??
            messages.VERSION_CONTROL.MERGE_MODAL.MERGE_REQUEST_ERROR_MESSAGE
          }
          onClose={onCancel}
        />
      );
    }

    if (hasParentCommitsAhead) {
      return (
        <UpdateRequiredModal
          onCancel={onCancel}
          onUpdateFromParent={getParentConflicts}
        />
      );
    }

    return (
      <MergeRequestBranchModal
        branchName={branchName ?? ''}
        onCancel={onCancel}
        onMergeBranch={() => createMergeRequest()}
      />
    );
  };

  return (
    <>
      <Backdrop />
      {renderModals()}
    </>
  );
};

const mapStateToProps = (state: unknown) => ({
  projectBranch: getProjectBranch(state),
  project: getProject(state),
});

export default connect(mapStateToProps, { refreshNLUState })(MergeBranchModal);
